import type { CSSProperties, ElementType, ReactNode } from 'react';
import type { LaoWeight, MonoWeight } from '@/theme/tokens';
import styles from './Text.module.css';

interface BaseProps {
  size?: number;
  color?: string;
  /** Shorthand for `text-align`. */
  align?: 'left' | 'center' | 'right';
  /** `numberOfLines` from the RN original: clamp to one or two lines. */
  lines?: 1 | 2;
  /** Renders as this element instead of `<span>` — e.g. `h1` for a screen title. */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  title?: string;
  id?: string;
}

export interface TextProps extends BaseProps {
  weight?: LaoWeight;
}

/**
 * Sizes are written at the call site as the handoff's raw numbers; this is the
 * only place they get scaled. `--k2-type-scale` lives in tokens.ts, so changing
 * the whole app's scale stays a one-constant change — and a media query can
 * rescale it for desktop without touching a single screen.
 */
function scaled(size: number): string {
  return `calc(${size} * var(--k2-type-scale) * 1px)`;
}

/**
 * Lao/UI text. Always use this rather than a bare element, so no screen can
 * fall through to the system font — which renders Lao tone marks poorly.
 */
export function Text({
  weight = 400,
  size = 13,
  color,
  align,
  lines,
  as: Tag = 'span',
  className,
  style,
  ...rest
}: TextProps) {
  return (
    <Tag
      {...rest}
      className={[styles.lao, lines === 1 ? 'clamp1' : lines === 2 ? 'clamp2' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={{ fontSize: scaled(size), fontWeight: weight, color, textAlign: align, ...style }}
    />
  );
}

export interface MonoProps extends BaseProps {
  weight?: MonoWeight;
}

/** Space Mono — money figures, dates, IDs, quantities and micro uppercase labels. */
export function Mono({
  weight = 400,
  size = 13,
  color,
  align,
  lines,
  as: Tag = 'span',
  className,
  style,
  ...rest
}: MonoProps) {
  return (
    <Tag
      {...rest}
      className={[styles.mono, lines === 1 ? 'clamp1' : lines === 2 ? 'clamp2' : '', className]
        .filter(Boolean)
        .join(' ')}
      style={{ fontSize: scaled(size), fontWeight: weight, color, textAlign: align, ...style }}
    />
  );
}
